import { reportApiError } from "@/lib/observability/datadog"

export class ApiError extends Error {
  readonly status: number
  readonly detail?: unknown

  constructor(message: string, status: number, detail?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.detail = detail
  }
}

/**
 * A non-empty string field off a failed request's JSON body, else
 * `undefined`. The one reader for structured error bodies.
 */
export function apiErrorField(err: unknown, key: string): string | undefined {
  const value = apiErrorValue(err, key)
  return typeof value === "string" && value ? value : undefined
}

function apiErrorValue(err: unknown, key: string): unknown {
  if (!(err instanceof ApiError)) return undefined
  const body = err.detail
  if (typeof body !== "object" || body === null) return undefined
  return (body as Record<string, unknown>)[key]
}

/**
 * A numeric field off a failed request's JSON body (e.g. `row_index`).
 * No truthiness check: `0` is an ordinary value naming the first row.
 */
export function apiErrorNumber(
  err: unknown,
  key: string,
): number | undefined {
  const value = apiErrorValue(err, key)
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"])
const CSRF_COOKIE_NAME = "csrftoken"

function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null
  const needle = `${CSRF_COOKIE_NAME}=`
  for (const raw of document.cookie.split(";")) {
    const entry = raw.trim()
    if (entry.startsWith(needle)) {
      return decodeURIComponent(entry.slice(needle.length))
    }
  }
  return null
}

function hasHeader(
  headers: Record<string, string>,
  name: string,
): boolean {
  const needle = name.toLowerCase()
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === needle) return true
  }
  return false
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase()
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  }

  if (UNSAFE_METHODS.has(method)) {
    if (!hasHeader(headers, "X-CSRFToken")) {
      const token = readCsrfToken()
      if (token) headers["X-CSRFToken"] = token
    }
    // String bodies only: FormData / Blob / URLSearchParams need the browser
    // to compute the boundary.
    if (
      typeof init?.body === "string" &&
      !hasHeader(headers, "Content-Type")
    ) {
      headers["Content-Type"] = "application/json"
    }
  }

  let response: Response
  try {
    response = await fetch(path, { ...init, headers })
  } catch (err) {
    // Network-level failure (DNS, offline, CORS, abort); `status: 0` marks
    // "no response".
    reportApiError({ method, path, status: 0, detail: undefined })
    throw err
  }

  if (!response.ok) {
    let detail: unknown
    try {
      detail = await response.json()
    } catch {
      detail = undefined
    }
    reportApiError({ method, path, status: response.status, detail })
    throw new ApiError(
      `${response.status} ${response.statusText}`,
      response.status,
      detail,
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}
